import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Accordion } from "react-bootstrap";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import ApiRequest from "../../helper/ApiRequest";
import CustomSelect from "../../component/CustomSelect";
import CustomImageUpload from "../../component/CustomImageUpload";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";

const editEndpoint = ApiEndpoints.disclosure.Edit;
const saveTabBase = ApiEndpoints.disclosure.SaveTab;

const tabConfigs = [
  { key: "first", label: "Tab 1", isTextTab: true },
  { key: "second", label: "Tab 2", isTextTab: false },
  { key: "third", label: "Tab 3", isTextTab: false },
  { key: "fourth", label: "Tab 4", isTableTab: true },
  { key: "fifth", label: "Tab 5", isTableTab: true },
  { key: "sixth", label: "Tab 6", isTextTab: true },
  { key: "seventh", label: "Tab 7", isTextTab: true },
];

const emptyRow = (tab) => {

  // TABLE TABS
  if (tab.isTableTab) {
    return {
      year: "",
      registered_student: "",
      student_passed: "",
      percentage: "",
    };
  }

  // TEXT TAB
  if (tab.isTextTab) {
    return {
      information: "",
      details: "",
    };
  }

  // FILE TAB
  return {
    information: "",
    document: null,
  };
};

export default function DisclosureEdit() {

  const { disclosure_id } = useParams();

  const [branches, setBranches] = useState([]);
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    branch: [],

    main_disclosure: null,

    first_tab_title: "",
    first_tab_info: [],

    second_tab_title: "",
    second_tab_info: [],

    third_tab_title: "",
    third_tab_info: [],

    fourth_tab_title: "",
    fourth_tab_info: [],

    fifth_tab_title: "",
    fifth_tab_info: [],

    sixth_tab_title: "",
    sixth_tab_info: [],

    seventh_tab_title: "",
    seventh_tab_info: [],
  });

  // FETCH DATA
  useEffect(() => {

    const fetchData = async () => {

      try {

        const res = await ApiRequest(
          "GET",
          `${editEndpoint}${disclosure_id}`
        );

        const d = res.data;

        setBranches(res.branches || []);

        setFormData({

          branch: d.branch?.map((b) => b._id) || [],

          main_disclosure: d.main_disclosure || null,

          first_tab_title: d.first_tab_title || "",
          first_tab_info: d.first_tab_info || [],

          second_tab_title: d.second_tab_title || "",
          second_tab_info: d.second_tab_info || [],

          third_tab_title: d.third_tab_title || "",
          third_tab_info: d.third_tab_info || [],

          fourth_tab_title: d.fourth_tab_title || "",
          fourth_tab_info: d.fourth_tab_info || [],

          fifth_tab_title: d.fifth_tab_title || "",
          fifth_tab_info: d.fifth_tab_info || [],

          sixth_tab_title: d.sixth_tab_title || "",
          sixth_tab_info:
            d.sixth_tab_info?.map((item) => ({
              ...item,
              old_document: item.document || null,
            })) || [],

          seventh_tab_title: d.seventh_tab_title || "",
          seventh_tab_info: d.seventh_tab_info || [],

        });

      } catch (error) {

        console.error("Fetch error:", error);
        toast.error("Failed to load record.");

      } finally {

        setLoading(false);

      }
    };

    fetchData();

  }, [disclosure_id]);

  // HANDLERS
  const handleBranchChange = (e) => {

    const ids = e.target.value
      ? e.target.value.split(",").filter(Boolean)
      : [];

    setFormData((prev) => ({
      ...prev,
      branch: ids,
    }));
  };

  const handleTitleChange = (tabKey, value) => {

    setFormData((prev) => ({
      ...prev,
      [`${tabKey}_tab_title`]: value,
    }));
  };

  const handleRowChange = (tabKey, index, field, value) => {

    setFormData((prev) => {

      const key = `${tabKey}_tab_info`;

      const updated = [...prev[key]];

      updated[index] = {
        ...updated[index],
        [field]: value,
      };

      return {
        ...prev,
        [key]: updated,
      };
    });
  };

  const addRow = (tab) => {

    setFormData((prev) => {

      const key = `${tab.key}_tab_info`;

      return {
        ...prev,
        [key]: [...prev[key], emptyRow(tab)],
      };
    });
  };

  const removeRow = (tabKey, index) => {

    setFormData((prev) => {

      const key = `${tabKey}_tab_info`;

      return {
        ...prev,
        [key]: prev[key].filter((_, i) => i !== index),
      };
    });
  };

  // BUILD FORM DATA
  const buildTabFormData = (
    tabKey,
    isTextTab,
    isTableTab
  ) => {

    const fd = new FormData();

    fd.append(
      "branch",
      JSON.stringify(formData.branch)
    );

    fd.append(
      `${tabKey}_tab_title`,
      formData[`${tabKey}_tab_title`]
    );

    if (
      tabKey === "first" &&
      formData.main_disclosure instanceof File
    ) {

      fd.append(
        "main_disclosure",
        formData.main_disclosure
      );
    }

    const rows = formData[`${tabKey}_tab_info`];

    // TABLE TAB
    if (isTableTab) {

      fd.append(
        `${tabKey}_tab_info`,
        JSON.stringify(
          rows.map((row) => ({
            year: row.year,
            registered_student: row.registered_student,
            student_passed: row.student_passed,
            percentage: row.percentage,
          }))
        )
      );

      return fd;
    }

    // =========================
    // SIXTH TAB
    // =========================
    if (tabKey === "sixth") {

      fd.append(
        `${tabKey}_tab_info`,
        JSON.stringify(
          rows.map((row) => ({
            information: row.information,
            details: row.details,

            // preserve old document
            document:
              row.document instanceof File
                ? row.old_document || null
                : row.document || null,
          }))
        )
      );

      rows.forEach((row, index) => {

        if (row.document instanceof File) {

          fd.append(
            `${tabKey}_tab_info_document`,
            row.document
          );

          // IMPORTANT
          fd.append(
            `${tabKey}_tab_info_document_index`,
            index.toString()
          );
        }

      });

      return fd;
    }

    // =========================
    // NORMAL TEXT TAB
    // =========================
    if (isTextTab) {

      fd.append(
        `${tabKey}_tab_info`,
        JSON.stringify(
          rows.map(({ information, details }) => ({
            information,
            details,
          }))
        )
      );

      return fd;
    }

    // =========================
    // FILE TAB
    // =========================
    fd.append(
      `${tabKey}_tab_info`,
      JSON.stringify(
        rows.map((row) => ({
          information: row.information,
          document:
            row.document instanceof File
              ? row.old_document || null
              : row.document || null,
        }))
      )
    );

    rows.forEach((row, index) => {

      if (row.document instanceof File) {

        fd.append(
          `${tabKey}_tab_info_document`,
          row.document
        );

        fd.append(
          `${tabKey}_tab_info_document_index`,
          index.toString()
        );
      }

    });

    return fd;
  };

  // SAVE
  const handleSave = async (
    tabKey,
    isTextTab,
    isTableTab
  ) => {

    // VALIDATION
    if (!formData.branch.length) {
      toast.error("Please select at least one branch.");
      return;
    }

    if (!formData[`${tabKey}_tab_title`].trim()) {
      toast.error("Tab title is required.");
      return;
    }

    const infoRows = formData[`${tabKey}_tab_info`];

    if (!infoRows.length) {
      toast.error("At least one row is required.");
      return;
    }

    for (let i = 0; i < infoRows.length; i++) {

      const row = infoRows[i];

      // TABLE TAB
      if (isTableTab) {

        if (!row.year?.trim()) {
          toast.error(`Row ${i + 1}: Year is required.`);
          return;
        }

        if (!row.registered_student?.trim()) {
          toast.error(`Row ${i + 1}: Registered Student is required.`);
          return;
        }

        if (!row.student_passed?.trim()) {
          toast.error(`Row ${i + 1}: Student Passed is required.`);
          return;
        }

        if (!row.percentage?.trim()) {
          toast.error(`Row ${i + 1}: Percentage is required.`);
          return;
        }

        continue;
      }

      // TEXT + FILE TAB
      if (!row.information?.trim()) {
        toast.error(`Row ${i + 1}: Information is required.`);
        return;
      }

      if (isTextTab) {

        if (!row.details?.trim()) {
          toast.error(`Row ${i + 1}: Details is required.`);
          return;
        }

        // sixth tab document optional
        if (tabKey === "sixth") {
          continue;
        }

      } else {

        if (!row.document) {
          toast.error(`Row ${i + 1}: Document is required.`);
          return;
        }

      }
    }

    setSaving(tabKey);

    try {

      const fd = buildTabFormData(
        tabKey,
        isTextTab,
        isTableTab
      );

      const tabEndpoint =
        `${saveTabBase}${disclosure_id}/tab/${tabKey}`;

      const response = await ApiRequest(
        "POST",
        tabEndpoint,
        fd
      );

      if (response.status === true) {

        toast.success(
          response?.message || "Updated successfully"
        );

      } else {

        toast.error(
          response?.message || "Failed to update."
        );

      }

    } catch (error) {

      console.error("Save error:", error);

      toast.error(
        error?.response?.data?.message ||
        "Something went wrong."
      );

    } finally {

      setSaving(null);

    }
  };

  const pagename = {
    name: "Mandatory Disclosure",
    user: "Admin",
    innerpage: "Mandatory Disclosure / Edit",
  };

  if (loading) {
    return (
      <div className="add-content-page">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      <AdminHeader pagename={pagename} />

      <div className="add-content-page">

        {/* BRANCH */}
        <div className="add-content-wrap">
          <div className="row">

            <div className="col-lg-6">
              <div className="add-page-inputs">
                <div className="label-wrap-add">
                  <label>
                    Select Multiple Branch<small>*</small>
                  </label>
                </div>

                <CustomSelect
                  name="branch"
                  options={branches}
                  placeholder="Select Branch"
                  multiple={true}
                  defaultValue={formData.branch}
                  onChange={handleBranchChange}
                />
              </div>
            </div>

            <div className="col-lg-6">
              <div className="add-page-inputs">
                <div className="label-wrap-add">
                  <label>
                    Disclosure Document (PDF/Word)
                    <small>*</small>
                  </label>
                </div>

                <CustomImageUpload
                  name="main_disclosure"
                  acceptedFormats={[
                    ".pdf",
                    ".doc",
                    ".docx",
                  ]}
                  onImageChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      main_disclosure:
                        e.target.files[0] || null,
                    }))
                  }
                  imagePreview={
                    formData.main_disclosure instanceof File
                      ? formData.main_disclosure.name
                      : formData.main_disclosure
                        ? formData.main_disclosure
                          .split("/")
                          .pop()
                        : null
                  }
                />
              </div>
            </div>

          </div>
        </div>

        {/* ACCORDION */}
        <div className="add-content-wrap">
          <div className="row">
            <div className="col-lg-12">

              <div className="home-slove-accord">

                <Accordion defaultActiveKey="0">

                  {tabConfigs.map((tab, idx) => (

                    <Accordion.Item
                      eventKey={idx.toString()}
                      key={tab.key}
                    >

                      <Accordion.Header>
                        {formData[`${tab.key}_tab_title`] || tab.label}
                      </Accordion.Header>

                      <Accordion.Body>

                        {/* TITLE */}
                        <div className="row">
                          <div className="col-lg-12">

                            <div className="add-page-inputs">

                              <div className="label-wrap-add">
                                <label>
                                  Tab Title<small>*</small>
                                </label>
                              </div>

                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter tab title"
                                value={formData[`${tab.key}_tab_title`]}
                                onChange={(e) =>
                                  handleTitleChange(
                                    tab.key,
                                    e.target.value
                                  )
                                }
                              />

                            </div>

                          </div>
                        </div>

                        {/* ROWS */}
                        {formData[`${tab.key}_tab_info`].map((row, i) => (

                          <div
                            className="row align-items-end"
                            key={row._id || i}
                          >

                            {/* TABLE TAB */}
                            {tab.isTableTab ? (
                              <>

                                <div className="col-lg-2">
                                  <div className="add-page-inputs">
                                    <div className="label-wrap-add">
                                      <label>
                                        Year
                                        <small>*</small>
                                      </label>
                                    </div>

                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Enter Year"
                                      value={row.year || ""}
                                      onChange={(e) =>
                                        handleRowChange(
                                          tab.key,
                                          i,
                                          "year",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                </div>

                                <div className="col-lg-3">
                                  <div className="add-page-inputs">
                                    <div className="label-wrap-add">
                                      <label>
                                        Registered Student
                                        <small>*</small>
                                      </label>
                                    </div>

                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Enter Registered Student"
                                      value={row.registered_student || ""}
                                      onChange={(e) =>
                                        handleRowChange(
                                          tab.key,
                                          i,
                                          "registered_student",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                </div>

                                <div className="col-lg-3">
                                  <div className="add-page-inputs">
                                    <div className="label-wrap-add">
                                      <label>
                                        Student Passed
                                        <small>*</small>
                                      </label>
                                    </div>

                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Enter Student Passed"
                                      value={row.student_passed || ""}
                                      onChange={(e) =>
                                        handleRowChange(
                                          tab.key,
                                          i,
                                          "student_passed",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                </div>

                                <div className="col-lg-2">
                                  <div className="add-page-inputs">
                                    <div className="label-wrap-add">
                                      <label>
                                        Percentage
                                        <small>*</small>
                                      </label>
                                    </div>

                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Enter Percentage"
                                      value={row.percentage || ""}
                                      onChange={(e) =>
                                        handleRowChange(
                                          tab.key,
                                          i,
                                          "percentage",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                </div>

                              </>
                            ) : (
                              <>
                                {/* INFORMATION */}
                                <div className={tab.key === "sixth" ? "col-lg-3" : "col-lg-4"}>

                                  <div className="add-page-inputs">

                                    <div className="label-wrap-add">
                                      <label>
                                        Information<small>*</small>
                                      </label>
                                    </div>

                                    <input
                                      type="text"
                                      className="form-control"
                                      value={row.information || ""}
                                      onChange={(e) =>
                                        handleRowChange(
                                          tab.key,
                                          i,
                                          "information",
                                          e.target.value
                                        )
                                      }
                                    />

                                  </div>

                                </div>

                                {/* TEXT TAB */}
                                {tab.isTextTab ? (

                                  <>

                                    <div className="col-lg-3">

                                      <div className="add-page-inputs">

                                        <div className="label-wrap-add">
                                          <label>
                                            Details<small>*</small>
                                          </label>
                                        </div>

                                        <input
                                          type="text"
                                          className="form-control"
                                          value={row.details || ""}
                                          onChange={(e) =>
                                            handleRowChange(
                                              tab.key,
                                              i,
                                              "details",
                                              e.target.value
                                            )
                                          }
                                        />

                                      </div>

                                    </div>

                                    {/* ONLY SIXTH TAB */}
                                    {tab.key === "sixth" && (

                                      <div className="col-lg-4">

                                        <div className="add-page-inputs input-add-image customimage-gap">

                                          <div className="label-wrap-add">
                                            <label>
                                              Document (Optional)
                                            </label>
                                          </div>

                                          <CustomImageUpload
                                            name="document"
                                            acceptedFormats={[
                                              ".pdf",
                                              ".doc",
                                              ".docx"
                                            ]}
                                            onImageChange={(e) =>
                                              handleRowChange(
                                                tab.key,
                                                i,
                                                "document",
                                                e.target.files[0] || null
                                              )
                                            }
                                            imagePreview={
                                              row.document instanceof File
                                                ? row.document.name
                                                : row.document
                                                  ? row.document.split("/").pop()
                                                  : null
                                            }
                                          />

                                        </div>

                                      </div>

                                    )}

                                  </>

                                ) : (

                                  <div className="col-lg-5">

                                    <div className="add-page-inputs input-add-image customimage-gap">

                                      <div className="label-wrap-add">
                                        <label>
                                          Document (PDF/Word)
                                          <small>*</small>
                                        </label>
                                      </div>

                                      <CustomImageUpload
                                        name="document"
                                        acceptedFormats={[
                                          ".pdf",
                                          ".doc",
                                          ".docx"
                                        ]}
                                        onImageChange={(e) =>
                                          handleRowChange(
                                            tab.key,
                                            i,
                                            "document",
                                            e.target.files[0] || null
                                          )
                                        }
                                        imagePreview={
                                          row.document instanceof File
                                            ? row.document.name
                                            : row.document
                                              ? row.document.split("/").pop()
                                              : null
                                        }
                                      />

                                    </div>

                                  </div>

                                )}
                              </>
                            )}

                            {/* DELETE */}
                            <div className="col-lg-2">

                              <div className="add-page-inputs">

                                <button
                                  type="button"
                                  className="coustom-delete"
                                  onClick={() =>
                                    removeRow(tab.key, i)
                                  }
                                >
                                  <img
                                    src="/assets/admin-delete.webp"
                                    alt="remove"
                                    className="img-responsive"
                                  />
                                </button>

                              </div>

                            </div>

                          </div>

                        ))}

                        {/* BUTTONS */}
                        <div className="row">

                          <div className="col-lg-12 mt-2">

                            <div
                              className="addmore-images-client"
                              onClick={() => addRow(tab)}
                            >
                              Add Row +
                            </div>

                          </div>

                          <div className="col-lg-12">

                            <div className="btn-group-home-solve">

                              <button
                                type="button"
                                className="default-btn"
                                onClick={() =>
                                  handleSave(
                                    tab.key,
                                    tab.isTextTab,
                                    tab.isTableTab
                                  )
                                }
                                disabled={saving === tab.key}
                              >
                                {saving === tab.key
                                  ? "Updating..."
                                  : "Update"}

                                <span></span>

                              </button>

                            </div>

                          </div>

                        </div>

                      </Accordion.Body>

                    </Accordion.Item>

                  ))}

                </Accordion>

              </div>

            </div>
          </div>
        </div>

      </div>
    </>
  );
}